import {
  doc, collection, setDoc, getDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, getDocs, serverTimestamp,
  Timestamp, onSnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '../shared';
import type {
  UserProfile, Session, SessionStats, AggregatedStats, DrillAttempt
} from '../shared';

// ─── Users ────────────────────────────────────────────────────────

export async function createUserProfile(uid: string, data: Partial<UserProfile>): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.USERS, uid), {
    ...data,
    uid,
    createdAt: serverTimestamp(),
    subscriptionStatus: 'trial',
    subscriptionTier: 'free',
    trialEndsAt: Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
  });

  await setDoc(doc(db, 'customers', uid), {
    email: data.email ?? '',
  }, { merge: true });
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.USERS, uid));
  if (!snap.exists()) return null;
  return snap.data() as UserProfile;
}

export function subscribeToUserProfile(
  uid: string,
  callback: (profile: UserProfile | null) => void
): Unsubscribe {
  return onSnapshot(doc(db, COLLECTIONS.USERS, uid), snap => {
    callback(snap.exists() ? (snap.data() as UserProfile) : null);
  });
}

// ─── Sessions ─────────────────────────────────────────────────────

export async function createSession(uid: string, type: 'match' | 'practice'): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTIONS.SESSIONS), {
    uid,
    type,
    startedAt: serverTimestamp(),
    endedAt: null,
    shots: [],
    frames: [],
  });
  return ref.id;
}

export async function saveCompletedSession(
  sessionId: string,
  session: Partial<Session>
): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.SESSIONS, sessionId), {
    ...session,
    endedAt: serverTimestamp(),
  });
}

/**
 * Fetch recent sessions for History page.
 * Reads from sessionStats subcollection — the same source as Dashboard
 * and Progress — so all three pages show consistent data.
 * Returns sessions newest-first.
 */
export async function getSessionHistory(uid: string, count = 50): Promise<SessionStats[]> {
  const q = query(
    collection(db, COLLECTIONS.USERS, uid, 'sessionStats'),
    orderBy('date', 'desc'),
    limit(count)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as SessionStats & { id: string }));
}

/**
 * Fetch recent session stats for Dashboard / Progress charts.
 * Returns sessions oldest-first (suitable for chart rendering).
 */
export async function getSessionStatsList(uid: string, count = 20): Promise<SessionStats[]> {
  const q = query(
    collection(db, COLLECTIONS.USERS, uid, 'sessionStats'),
    orderBy('date', 'desc'),
    limit(count)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as SessionStats);
}

/**
 * Hard-delete a session and its stats snapshot.
 * Deletes both the sessions doc and the sessionStats entry so that
 * Dashboard and Progress aggregates are recomputed correctly via
 * the onSessionStatsDeleted Cloud Function.
 */
export async function deleteSession(sessionId: string, uid: string): Promise<void> {
  await Promise.all([
    deleteDoc(doc(db, COLLECTIONS.SESSIONS, sessionId)),
    deleteDoc(doc(db, COLLECTIONS.USERS, uid, 'sessionStats', sessionId)),
  ]);
}

// ─── Stats ────────────────────────────────────────────────────────

export async function getAggregatedStats(uid: string): Promise<AggregatedStats | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.STATS, uid));
  if (!snap.exists()) return null;
  return snap.data() as AggregatedStats;
}

export function subscribeToAggregatedStats(
  uid: string,
  callback: (stats: AggregatedStats | null) => void
): Unsubscribe {
  return onSnapshot(doc(db, COLLECTIONS.STATS, uid), snap => {
    callback(snap.exists() ? (snap.data() as AggregatedStats) : null);
  });
}

// ─── Drills ───────────────────────────────────────────────────────

export async function saveDrillAttempt(attempt: Omit<DrillAttempt, 'id'>): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTIONS.DRILLS), {
    ...attempt,
    date: serverTimestamp(),
  });
  return ref.id;
}

export async function getDrillHistory(uid: string, drillId?: string): Promise<DrillAttempt[]> {
  const constraints: any[] = [
    where('uid', '==', uid),
    orderBy('date', 'desc'),
    limit(50),
  ];
  if (drillId) constraints.splice(1, 0, where('drillId', '==', drillId));
  const q = query(collection(db, COLLECTIONS.DRILLS), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as DrillAttempt));
}

// ─── Admin ────────────────────────────────────────────────────────

export async function getAllUsers(limitCount = 100): Promise<UserProfile[]> {
  const q = query(collection(db, COLLECTIONS.USERS), limit(limitCount));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as UserProfile);
}

// ─── Stripe / Subscription ────────────────────────────────────────

export async function createCheckoutSession(uid: string, priceId: string): Promise<string> {
  const sessionRef = await addDoc(
    collection(db, 'customers', uid, 'checkout_sessions'),
    {
      price: priceId,
      success_url: `${window.location.origin}/subscribe?success=true`,
      cancel_url:  `${window.location.origin}/subscribe?cancelled=true`,
      allow_promotion_codes: true,
    }
  );

  return new Promise((resolve, reject) => {
    const unsub = onSnapshot(sessionRef, (snap) => {
      const data = snap.data();
      if (data?.error) { unsub(); reject(new Error(data.error.message)); }
      if (data?.url)   { unsub(); resolve(data.url as string); }
    });
  });
}

export async function createPortalSession(uid: string): Promise<string> {
  const portalRef = await addDoc(
    collection(db, 'customers', uid, 'checkout_sessions'),
    {
      return_url: `${window.location.origin}/subscribe`,
      mode: 'portal',
    }
  );

  return new Promise((resolve, reject) => {
    const unsub = onSnapshot(portalRef, (snap) => {
      const data = snap.data();
      if (data?.error) { unsub(); reject(new Error(data.error.message)); }
      if (data?.url)   { unsub(); resolve(data.url as string); }
    });
  });
}